// useAuthSession hook + apiFetch instance + signOut helper.
//
// Replaces the legacy `auth-session.jsx` IIFE that pinned globals on
// `window.{apiFetch, useAuthSession, signOut}`. Same semantics; same
// degraded-fallback behaviour when /api/auth/session fails.
//
// Single global cache so multiple components share one Firebase auth
// subscription — matches the legacy module's behaviour.

import { useEffect, useState } from 'react';
import { onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import {
  createApiFetch,
  normalizeRole,
  type ApiFetch,
  type AuthState,
  type SessionUser,
} from '@nayto/auth';
import { getFirebaseAuth } from './firebase';

// ─── apiFetch ────────────────────────────────────────────────────────
//
// baseUrl='' so requests stay same-origin: dev hits Vite's `/api/*` proxy,
// prod hits Vercel's `/api/*` rewrite. VITE_API_BASE_URL only configures
// the dev proxy target (see apps/web/vite.config.ts).
//
// 401 handler signs the user out — onAuthStateChanged will then flip the
// hook to 'signed-out' and the route shell will route back to onboarding.
const getToken = async (): Promise<string | null> => {
  const user = getFirebaseAuth().currentUser;
  return user ? await user.getIdToken(false) : null;
};

export const apiFetch: ApiFetch = createApiFetch({
  baseUrl: '',
  getToken,
  on401: () => {
    void firebaseSignOut(getFirebaseAuth()).catch((err) => {
      console.warn('signOut after 401 failed', err);
    });
  },
});

// Same request, no auth gate. For background polls whose failure must
// not cost the user their session — today that is `GET /api/services`
// (see ../services-context). A config endpoint that is missing, or that
// a misconfigured proxy answers with 401, must never eject someone from
// the translator mid-upload.
export const apiFetchQuiet: ApiFetch = createApiFetch({ baseUrl: '', getToken });

// ─── useAuthSession ──────────────────────────────────────────────────

let cachedState: AuthState = { status: 'loading', user: null };
const listeners = new Set<(state: AuthState) => void>();

function setState(next: AuthState): void {
  cachedState = next;
  for (const listener of listeners) listener(next);
}

let started = false;
function start(): void {
  if (started) return;
  started = true;

  const auth = getFirebaseAuth();
  onAuthStateChanged(auth, async (firebaseUser) => {
    if (!firebaseUser) {
      setState({ status: 'signed-out', user: null });
      return;
    }
    // Hit /api/auth/session once per sign-in to upsert our app_users doc
    // and pull the role/handles/partyAccess profile.
    try {
      const res = await apiFetch('/api/auth/session', { method: 'POST' });
      if (!res.ok) throw new Error(`session failed: ${res.status}`);
      const { user } = (await res.json()) as { user: SessionUser };
      // normalizeRole: the server may still carry the processor-era
      // 'gray_user' on old rows. F-01 dropped that bucket, and an
      // unknown role would index ROLE_ROUTES with undefined and strip
      // the whole nav. Fold it to 'user' at the door.
      setState({ status: 'ready', user: { ...user, role: normalizeRole(user?.role) } });
    } catch (err) {
      console.error('session sync failed', err);
      // Soft-fail: Firebase user exists but server profile unavailable.
      // UI treats this as signed-in-but-degraded; signing out + back in
      // recovers. Matches legacy auth-session.jsx behaviour exactly.
      const phoneNumber = firebaseUser.phoneNumber ?? undefined;
      setState({
        status: 'ready',
        user: {
          _id: firebaseUser.uid,
          ...(phoneNumber ? { phoneNumber } : {}),
          role: 'user',
          degraded: true,
        },
      });
    }
  });
}

export function useAuthSession(): AuthState {
  const [state, setLocalState] = useState<AuthState>(() => {
    // Defer Firebase init to the first hook call — keeps modules that
    // merely import this file from triggering the SDK at module load.
    start();
    return cachedState;
  });
  useEffect(() => {
    listeners.add(setLocalState);
    return () => {
      listeners.delete(setLocalState);
    };
  }, []);
  return state;
}

// ─── refreshMe — mid-session role/handles refetch ────────────────────
//
// Calls GET /api/me to pull the latest server profile (role, handles,
// commentTrackingEnabled, …) without re-running the full /api/auth/session
// upsert. Use after an action that the server processes asynchronously —
// e.g. after onboarding submits handles (server creates the app_users row
// with role='pending') or after an admin approves the user (role flips
// from 'pending' to admin/manager/user) and the user wants the
// shell to update without a full reload.
//
// Two shape quirks the backend exposes (and we normalize here):
//   1. /api/me returns the user *flat*, not wrapped in `{ user: {...} }`
//      like /api/auth/session does. Destructuring `user` would have left
//      the cached state nulled out and stripped the entire nav.
//   2. The id field is `uid` on /api/me (Firebase UID) but `_id` on
//      /api/auth/session. Map to `_id` so SessionUser stays uniform.
export async function refreshMe(): Promise<SessionUser | null> {
  if (cachedState.status !== 'ready' || !cachedState.user) return null;
  try {
    const res = await apiFetch('/api/me');
    if (!res.ok) throw new Error(`me failed: ${res.status}`);
    const raw = (await res.json()) as Record<string, unknown> & {
      user?: SessionUser & { uid?: string };
    };
    const flat: (SessionUser & { uid?: string }) | undefined =
      raw.user ?? (raw as unknown as SessionUser & { uid?: string });
    // Defensive: if the server response doesn't carry a role we can use,
    // keep the existing cached user — better than nulling out the nav.
    if (!flat || !flat.role) {
      console.warn('refreshMe: response missing role, keeping cached user', raw);
      return cachedState.user;
    }
    const user: SessionUser = {
      ...flat,
      role: normalizeRole(flat.role),
      _id: flat._id ?? flat.uid ?? cachedState.user._id,
    };
    setState({ status: 'ready', user });
    return user;
  } catch (err) {
    console.warn('refreshMe failed', err);
    return null;
  }
}

// ─── signOut ─────────────────────────────────────────────────────────

export async function signOut(): Promise<void> {
  await firebaseSignOut(getFirebaseAuth());
  // onAuthStateChanged fires → state flips to 'signed-out'.
}
